import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearSession,
  getPermissions,
  getToken,
  fetchAndStoreUserContext,
  syncAnalyticsContext,
} from '@/services/auth';
import { setUnauthorizedHandler } from '@/services/api';
import { PermissionsProvider } from '@/app/permissions';
import { AppRoutes } from '@/app/routes';
import { LoginScreen } from '@/pages/login/LoginScreen';
import { identifyUser, resetAnalytics, track } from '@/lib/analytics';

/**
 * Auth boundary. Everything below this is a signed-in session.
 *
 * v1 read `getToken()` from localStorage on every render inside a
 * `<PrivateRoute>`, which meant a 401 in a background request could not clear
 * the UI without a full `window.location` reload. Here the token is state, and
 * the API client calls back into it.
 */
export function App() {
  const [token, setToken] = useState<string | null>(getToken);
  const [permissions, setPermissions] = useState<string[]>(getPermissions);
  const queryClient = useQueryClient();

  const signOut = useCallback(() => {
    clearSession();
    setToken(null);
    setPermissions([]);
    // Drop every cached response — the next user on this shared office machine
    // must not see the previous one's students.
    queryClient.clear();
    /* And drop the analytics identity with it, for the same reason: without
       this the next clerk's events are filed under the previous clerk's name. */
    track('Signed Out', {});
    resetAnalytics();
  }, [queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(signOut);
  }, [signOut]);

  useEffect(() => {
    if (!token) return;
    /* The tenant ids are what make the numbers segmentable — "collections are
       down" is not actionable, "down at these three schools" is.

       Registered twice, deliberately. Once now, from whatever is already in
       storage, because `/api/me/context` is a round trip and every event fired
       while it is in flight — the first `Screen Viewed` above all — used to
       go out with no school on it at all. Then again when it lands, which is
       what corrects the ids after a first sign-in on a fresh machine. */
    syncAnalyticsContext();
    void fetchAndStoreUserContext().catch(() => undefined).finally(syncAnalyticsContext);
  }, [token]);

  if (!token) {
    return (
      <LoginScreen
        onSignedIn={(data) => {
          setToken(data.token);
          setPermissions(data.permissions);
          // Identified only when the server sends a user id; anonymous otherwise.
          if (data.id) identifyUser({ id: data.id, role: data.role });
          /* Before the event, not after. `resetAnalytics()` on the previous
             sign-out cleared the super properties, so without this `Signed In`
             — on a shared fee counter, every sign-in of the day — carries no
             school and no `app_version`, and cannot be told apart from v1's. */
          syncAnalyticsContext();
          track('Signed In', { role: data.role });
        }}
      />
    );
  }

  return (
    <PermissionsProvider permissions={permissions}>
      <AppRoutes onSignOut={signOut} />
    </PermissionsProvider>
  );
}
